import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import {
  useCreateDiagnosisGroup,
  useUpdateDiagnosisGroup,
} from "../../../stores/MasterTables/therapies.mutations";
import { useRxTypes } from "../../../stores/MasterTables/therapies.queries";
import type { DiagnosisGroupAdmin } from "./types";

interface ModalDiagnosisGroupProps {
  open: boolean;
  onClose: () => void;
  editItem: DiagnosisGroupAdmin | null;
}

const emptyForm = {
  code: "",
  description: "",
  a: "",
  b: "",
  c: "",
  d: "",
  isRelevant: true,
  primaryCount: "",
  secondaryCount: "",
  rxType: "",
};

const ModalDiagnosisGroup: React.FC<ModalDiagnosisGroupProps> = ({ open, onClose, editItem }) => {
  const createGroup = useCreateDiagnosisGroup();
  const updateGroup = useUpdateDiagnosisGroup();
  const { data: rxTypes = [] } = useRxTypes();

  const [form, setForm] = useState(emptyForm);
  const isEdit = !!editItem;

  useEffect(() => {
    if (open) {
      if (editItem) {
        setForm({
          code: editItem.code || "",
          description: editItem.description || "",
          a: editItem.a?.toString() || "",
          b: editItem.b?.toString() || "",
          c: editItem.c?.toString() || "",
          d: editItem.d?.toString() || "",
          isRelevant: editItem.isRelevant ?? true,
          primaryCount: editItem.PrimaryTreatments?.toString() || "",
          secondaryCount: editItem.SecondaryTreatments?.toString() || "",
          rxType: editItem.RxType?.id || "",
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, editItem]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCheckbox = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSubmit = async () => {
    const payload: any = {
      code: form.code,
      description: form.description,
      a: form.a || undefined,
      b: form.b || undefined,
      c: form.c || undefined,
      d: form.d || undefined,
      isRelevant: form.isRelevant,
      PrimaryTreatments: form.primaryCount || undefined,
      SecondaryTreatments: form.secondaryCount || undefined,
      rxTypeId: form.rxType || undefined,
    };

    try {
      if (isEdit) {
        await updateGroup.mutateAsync({ id: editItem!.id, data: payload });
      } else {
        await createGroup.mutateAsync({ id: form.code, ...payload });
      }
      onClose();
    } catch {
      // error handled in mutation
    }
  };

  const isPending = createGroup.isPending || updateGroup.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEdit ? "Diagnosegruppe bearbeiten" : "Neue Diagnosegruppe anlegen"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEdit ? `ID: ${editItem?.id}` : "Füllen Sie die Felder aus"}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Code *"
              value={form.code}
              onChange={handleChange("code")}
              fullWidth
              size="small"
              disabled={isEdit}
              placeholder="z.B. EX1"
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label="Beschreibung *"
              value={form.description}
              onChange={handleChange("description")}
              fullWidth
              size="small"
              placeholder="z.B. Erkrankungen der Extremitäten"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Verordnungsmengen (a–d)
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <TextField
              label="a"
              type="number"
              value={form.a}
              onChange={handleChange("a")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="b"
              type="number"
              value={form.b}
              onChange={handleChange("b")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="c"
              type="number"
              value={form.c}
              onChange={handleChange("c")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="d"
              type="number"
              value={form.d}
              onChange={handleChange("d")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Primär"
              type="number"
              value={form.primaryCount}
              onChange={handleChange("primaryCount")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Sekundär"
              type="number"
              value={form.secondaryCount}
              onChange={handleChange("secondaryCount")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={8}>
            <TextField
              select
              label="RxType"
              value={form.rxType}
              onChange={handleChange("rxType")}
              fullWidth
              size="small"
            >
              <MenuItem value="">– Keiner –</MenuItem>
              {rxTypes.map((rt: any) => (
                <MenuItem key={rt.id || rt} value={rt.id || rt}>
                  {rt.name || rt.id || rt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isRelevant}
                  onChange={handleCheckbox("isRelevant")}
                  color="success"
                />
              }
              label="Relevant"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} startIcon={<Cancel />} disabled={isPending}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          startIcon={<Save />}
          disabled={isPending || !form.code || !form.description}
        >
          {isPending ? "Speichert…" : isEdit ? "Aktualisieren" : "Anlegen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalDiagnosisGroup;
